import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, SentimentTrendResponse } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface PositivityByRepTrendChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

interface RepTrendData {
  name: string;
  id: string;
  trend: SentimentTrendResponse;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const formatPeriod = (period: string): string => {
  try {
    const date = new Date(period);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(2);
    return `${month} ${year}`;
  } catch {
    return period;
  }
};

const PositivityByRepTrendChart: React.FC<PositivityByRepTrendChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [repTrends, setRepTrends] = useState<RepTrendData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const effectiveRange = timeRange || '7d';
        const groupBy = effectiveRange === '90d' ? 'month' : 'week';

        // First fetch leaderboard to get top 5 salespeople
        const leaderboard = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          effectiveRange,
          'avg_interest',
          5
        );

        const topReps = leaderboard.leaderboard.slice(0, 5);

        if (topReps.length === 0) {
          setRepTrends([]);
          setIsLoading(false);
          return;
        }

        // Fetch sentiment trends for each rep using Promise.allSettled
        const trendPromises = topReps.map((rep) =>
          analyticsService.getSalespersonSentimentTrend(
            rep.salesperson_id,
            effectiveRange,
            groupBy
          )
        );

        const results = await Promise.allSettled(trendPromises);

        const trends: RepTrendData[] = [];
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            trends.push({
              name: topReps[idx].salesperson_name,
              id: topReps[idx].salesperson_id,
              trend: result.value,
            });
          }
        });

        setRepTrends(trends);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch positivity trend data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (repTrends.length === 0) return {};

    // Collect all unique periods across all reps
    const allPeriods = new Set<string>();
    repTrends.forEach((rep) => {
      rep.trend.data.forEach((d) => allPeriods.add(d.period));
    });
    const sortedPeriods = Array.from(allPeriods).sort();
    const periodLabels = sortedPeriods.map(formatPeriod);

    const series = repTrends.map((rep, idx) => {
      const periodMap = new Map(rep.trend.data.map((d) => [d.period, d.avg_score]));
      const values = sortedPeriods.map((p) => periodMap.get(p) ?? null);

      return {
        name: rep.name,
        type: 'line' as const,
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {
          color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
          width: 2,
        },
        itemStyle: {
          color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length],
        },
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        textStyle: { fontFamily: FONT_FAMILY },
      },
      legend: {
        bottom: 0,
        left: 'center',
        textStyle: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#718096',
        },
        itemWidth: 14,
        itemHeight: 3,
        itemGap: 16,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: periodLabels,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Positivity Score',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series,
    };
  };

  return (
    <ChartCard title="Positivity Score by Rep (Monthly)" isLoading={isLoading} error={error}>
      {repTrends.length > 0 ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            No data available
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default PositivityByRepTrendChart;
